import { useEffect, useRef, useState } from 'preact/hooks';
import { getSupabase } from '../../lib/supabaseClient';
import { savePendingPathFromLocation } from '../../data/membership';
import { isValidOtp } from './otp';
import { mapOtpSendError, mapOtpVerifyError } from '../errors';

type Props = {
  onSignedIn?(): void;
};

function normalizeEmail(v: string): string {
  return v.trim().toLowerCase();
}

export function SignIn(props: Props) {
  const [email, setEmail] = useState('');
  const [stage, setStage] = useState<'collect' | 'sent'>('collect');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState<number>(0);
  const resendTimerRef = useRef<number | null>(null);

  async function send() {
    setError(null);
    const sb = getSupabase();
    if (!sb) {
      console.error('[signIn send] backend not configured');
      setError(mapOtpSendError(new Error('backend_unconfigured')));
      return;
    }
    const e = normalizeEmail(email);
    if (!e) {
      setError('Enter your email');
      return;
    }
    setBusy(true);
    try {
      // Remember pending join route (if any) before sending
      savePendingPathFromLocation(window.location);
      const { error } = await sb.auth.signInWithOtp({
        email: e,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          shouldCreateUser: true
        }
      });
      if (error) {
        console.error('[signIn send]', error);
        setError(mapOtpSendError(error));
        return;
      }
      setStage('sent');
      // Start 60s resend cooldown
      setResendIn(60);
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
      resendTimerRef.current = window.setInterval(() => {
        setResendIn((s) => {
          if (s <= 1) {
            if (resendTimerRef.current) clearInterval(resendTimerRef.current);
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    } catch (err: any) {
      console.error('[signIn send]', err);
      setError(mapOtpSendError(err));
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, []);

  async function resend() {
    if (resendIn > 0) return;
    await send();
  }

  function onUseDifferentEmail() {
    setError(null);
    setEmail('');
    setCode('');
    setStage('collect');
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
      resendTimerRef.current = null;
    }
    setResendIn(0);
  }

  async function verify() {
    setError(null);
    const sb = getSupabase();
    if (!sb) {
      console.error('[signIn verify] backend not configured');
      setError(mapOtpVerifyError(new Error('backend_unconfigured')));
      return;
    }
    const e = normalizeEmail(email);
    if (!e) {
      setError('Enter your email');
      return;
    }
    const t = code.trim();
    if (!isValidOtp(t)) {
      setError('Enter the 8-digit code we emailed you');
      return;
    }
    setBusy(true);
    try {
      const { error } = await sb.auth.verifyOtp({
        email: e,
        token: t,
        type: 'email'
      });
      if (error) {
        console.error('[signIn verify]', error);
        setError(mapOtpVerifyError(error));
        return;
      }
      props.onSignedIn?.();
    } catch (err: any) {
      console.error('[signIn verify]', err);
      setError(mapOtpVerifyError(err));
    } finally {
      setBusy(false);
    }
  }

  // paste-link fallback removed

  return (
    <section class="install-safe">
      <div class="install-card">
        <h2 class="install-title">Sign in</h2>
        {stage === 'collect' && (
          <div class="install-body">
            <label class="form-label" for="email">Email</label>
            <input
              id="email"
              class="form-input"
              type="email"
              inputmode="email"
              autocomplete="email"
              value={email}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
              placeholder="you@example.com"
            />
            <div class="install-actions" style="margin-top:12px;">
              <button class="primary-btn" type="button" disabled={busy} onClick={() => void send()}>
                {busy ? 'Sending…' : 'Email me a code'}
              </button>
            </div>
            <p class="muted" style="margin-top:8px;">
              After you send the code, enter it here.
            </p>
            {error && <div class="tiny-banner error" role="alert">{error}</div>}
          </div>
        )}

        {stage === 'sent' && (
          <div class="install-body">
            <p class="muted">
              We sent a code to <strong>{email}</strong>.
            </p>
            <div style="margin:8px 0 12px;">
              <button class="secondary-btn" type="button" onClick={() => onUseDifferentEmail()}>
                Use a different email
              </button>
            </div>
            <label class="form-label" for="code">Enter the 8-digit code we emailed you</label>
            <input
              id="code"
              class="form-input"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              autocomplete="one-time-code"
              maxLength={8}
              value={code}
              onInput={(e) => {
                const raw = (e.target as HTMLInputElement).value;
                const digits = raw.replace(/\D/g, '').slice(0, 8);
                setCode(digits);
              }}
              placeholder="8-digit code"
            />
            <div class="install-actions" style="margin-top:12px;">
              <button class="primary-btn" type="button" disabled={busy} onClick={() => void verify()}>
                {busy ? 'Verifying…' : 'Verify code'}
              </button>
              <button
                class="secondary-btn"
                type="button"
                disabled={busy || resendIn > 0}
                onClick={() => void resend()}
                style="margin-left:8px;"
              >
                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend'}
              </button>
            </div>
            {error && <div class="tiny-banner error" role="alert">{error}</div>}
          </div>
        )}
      </div>
    </section>
  );
}

